import { FilterTypes } from "@/lib/types";

export interface Audit {
  condition: FilterTypes;
  result: boolean[];
  scores: number[];
  sub_audit?: Audit[];
  /** Post indices this node could not decide. Absent when empty.
   *
   * `result: false` for one of these posts means "we could not tell", NOT "no" — the engine
   * has to put SOMETHING in a boolean array, and false is what it puts. Reading it as a plain
   * no is how Debug ends up stating, in confident red, that a post would be hidden from a
   * feed when in truth nothing was ever evaluated.
   *
   * rust-smasher raises it for four reasons, none of them transient: a regex that would not
   * compile, a predicate score that could not be fetched, an unreachable adjudicator, and —
   * much the most common — a post that is missing the classifier enrichment a leaf needs.
   */
  undecided?: number[];
}

export interface AuditLog {
  audit: Audit;
  results: boolean[];
}

/** What this node actually says about the single post its arrays have been sliced to.
 *
 * One definition shared by every surface that colours something, because the interesting
 * case is the one a `result[0] ? … : …` ternary cannot express, and three independent
 * ternaries is three chances to forget it again.
 */
export function auditVerdict(
  audit: Audit | undefined,
): "success" | "failure" | "undecided" | "idle" {
  if (!audit) return "idle";
  // Checked BEFORE the boolean: an undecided post always carries `result: false`, so asking
  // the boolean first can only ever give the wrong answer.
  if (audit.undecided?.includes(0)) return "undecided";
  return audit.result[0] ? "success" : "failure";
}
